/* ==========================================================================
   Custom-brands index behaviour
   Reads the JSON printed by sections/custom-brands-index.liquid, then owns
   search / filter / sort / render. Setup tiles rendered by
   sections/custom-brands-setups.liquid act as filters through a document-level
   click handler, so the two sections stay independent in the theme editor.
   ========================================================================== */
(() => {
  const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* ISO code → localized country name + flag, same approach as single-brand.js */
  let regionNames = null;
  try {
    regionNames = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
  } catch (_) {
    regionNames = null;
  }
  const countryName = (code) => {
    if (!/^[A-Z]{2}$/.test(code)) return '';
    try {
      return (regionNames && regionNames.of(code)) || '';
    } catch (_) {
      return '';
    }
  };
  const countryFlag = (code) =>
    /^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...Array.from(code, (l) => 127397 + l.charCodeAt(0))) : '';

  function init(root) {
    if (root.dataset.cbiReady === '1') return;
    root.dataset.cbiReady = '1';

    /* Document-level listeners are tracked so a theme-editor reload can drop them. */
    const docListeners = [];
    const onDoc = (type, fn) => {
      document.addEventListener(type, fn);
      docListeners.push([type, fn]);
    };
    root.cbiTeardown = () => {
      docListeners.forEach(([type, fn]) => document.removeEventListener(type, fn));
      delete root.dataset.cbiReady;
    };

    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    let raw = [];
    try {
      raw = JSON.parse($('[data-cbi-brands]').textContent) || [];
    } catch (_) {
      raw = [];
    }
    if (!raw.length) return;

    const showCounts = root.dataset.showCounts !== 'false';
    const productsLabel = root.dataset.productsLabel || 'products';
    const SUGGESTIONS = (root.dataset.suggestions || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    /* ---------------------------------------------------- normalise ----- */
    const BRANDS = raw
      .map((b) => {
        const types = (b.types || []).filter(Boolean);
        const origin = (b.origin || '').toUpperCase();
        const country = countryName(origin);
        const name = b.name || b.handle;
        return {
          name,
          handle: b.handle,
          url: b.url || '#',
          count: b.count || 0,
          position: b.position || 0,
          desc: b.desc || '',
          origin,
          country,
          flag: countryFlag(origin),
          year: b.year || 0,
          types,
          letter: name.charAt(0).toUpperCase(),
          hay: [name, types.join(' '), origin, country, b.year, b.keywords || ''].join(' ').toLowerCase(),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    /* Filter chips, ordered by how much of the catalogue each type covers. */
    const counts = {};
    BRANDS.forEach((b) => b.types.forEach((t) => (counts[t] = (counts[t] || 0) + 1)));
    const TYPES = Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));

    /* Setups come from the custom-brands-setups section(s) on the page. */
    const byHandle = {};
    BRANDS.forEach((b) => (byHandle[b.handle] = b));
    const setupTiles = Array.from(document.querySelectorAll('[data-custom-brands-setup]'));
    const SETUPS = setupTiles.map((tile, i) => {
      tile.dataset.setupIndex = String(i);
      const members = String(tile.dataset.setupHandles || '')
        .split(',')
        .map((h) => h.trim())
        .filter((h) => h && byHandle[h]);
      const set = {};
      members.forEach((h) => (set[h] = 1));
      return { name: tile.dataset.setupName || 'Setup', members, set, tile };
    });

    const state = {
      q: '',
      types: [],
      setup: null,
      sort: 'az',
      density: root.dataset.density === 'grid' ? 'grid' : 'list',
    };

    /* -------------------------------------------------------- chips ----- */
    const chipsEl = $('[data-cbi-chips]');
    if (chipsEl) {
      chipsEl.innerHTML = TYPES.map(
        (t) =>
          `<button class="custom-brands-index__chip" type="button" aria-pressed="false" data-type="${esc(t)}">${esc(t)} <span class="custom-brands-index__chip-n" data-n="${esc(t)}"></span></button>`
      ).join('');
    }

    /* ---------------------------------------------------- alphabet ------ */
    const abcEl = $('[data-cbi-abc]');
    if (abcEl) {
      abcEl.innerHTML = LETTERS.map((L) => `<button type="button" data-letter="${L}">${L}</button>`).join('');
    }

    /* ----------------------------------------------------- matching ----- */
    function matches(b, q, types, setup) {
      if (setup !== null && setup !== undefined && !SETUPS[setup].set[b.handle]) return false;
      if (types.length && !types.some((t) => b.types.indexOf(t) > -1)) return false;
      if (q) {
        const toks = q.toLowerCase().split(/\s+/).filter(Boolean);
        for (let i = 0; i < toks.length; i++) if (b.hay.indexOf(toks[i]) === -1) return false;
      }
      return true;
    }

    /* "matched 808" — tells the shopper why a brand came back for a term
       that isn't in its name. Relies on the search_keywords field. */
    function viaLabel(b, q) {
      if (!q) return '';
      const low = q.toLowerCase().trim();
      const lname = b.name.toLowerCase();
      if (lname.indexOf(low) > -1) return '';
      const hits = [];
      low
        .split(/\s+/)
        .filter(Boolean)
        .forEach((t) => {
          if (lname.indexOf(t) > -1) return;
          if (b.country && b.country.toLowerCase().indexOf(t) > -1) {
            hits.push(b.country);
            return;
          }
          const ty = b.types.filter((x) => x.toLowerCase().indexOf(t) > -1)[0];
          if (ty) {
            hits.push(ty);
            return;
          }
          const kw = b.hay.split(' ').filter((w) => w.indexOf(t) > -1)[0];
          if (kw) hits.push(kw);
        });
      return hits.length ? `“${hits.slice(0, 2).join(', ')}”` : '';
    }

    function markName(name, q) {
      const t = (q || '').trim();
      if (!t) return esc(name);
      const i = name.toLowerCase().indexOf(t.toLowerCase());
      if (i < 0) return esc(name);
      return `${esc(name.slice(0, i))}<mark>${esc(name.slice(i, i + t.length))}</mark>${esc(name.slice(i + t.length))}`;
    }

    const tagHtml = (b) => b.types.map((t) => `<span class="custom-brands__tag">${esc(t)}</span>`).join('');
    const rowTags = (b) => {
      const visible = b.types.slice(0, 2).map((t) => `<span class="custom-brands__tag">${esc(t)}</span>`).join('');
      const extra = b.types.slice(2);
      if (!extra.length) return visible;
      return visible + `<details class="custom-brands-index__more-types"><summary class="custom-brands__tag" aria-label="Show ${extra.length} more types for ${esc(b.name)}">+${extra.length}</summary><span class="custom-brands-index__extra-types">${extra.map((t) => `<span class="custom-brands__tag">${esc(t)}</span>`).join('')}</span></details>`;
    };
    onDoc('keydown', (event) => {
      if (event.key !== 'Escape') return;
      root.querySelectorAll('.custom-brands-index__more-types[open]').forEach((details) => {
        details.open = false;
        details.querySelector('summary').focus();
      });
    });
    const metaText = (b) => {
      const place = b.country ? `${b.flag} ${b.country}` : b.origin;
      return [place, b.year || ''].filter(Boolean).join(' · ');
    };

    /* ------------------------------------------------------ renderers --- */
    function row(b, q, rank) {
      const via = viaLabel(b, q);
      return (
        `<div class="custom-brands-index__row" id="cbi-${esc(b.handle)}">` +
        `<a href="${esc(b.url)}" class="custom-brands-index__row-name">${rank ? `<span class="custom-brands-index__rank-col">${rank}</span> ` : ''}${markName(b.name, q)}</a>` +
        (via ? `<span class="custom-brands-index__row-via">${esc(via)}</span>` : '') +
        `<span class="custom-brands-index__row-desc">${esc(b.desc)}</span>` +
        `<span class="custom-brands-index__row-tags">${rowTags(b)}</span>` +
        `<span class="custom-brands-index__row-meta">${esc(metaText(b))}</span>` +
        (showCounts ? `<span class="custom-brands-index__row-count">${b.count}<small>${esc(productsLabel)}</small></span>` : '') +
        '<span class="custom-brands-index__row-go" aria-hidden="true">›</span>' +
        '</div>'
      );
    }

    function tile(b, q, rank) {
      const via = viaLabel(b, q);
      return (
        `<a class="custom-brands-index__tile" href="${esc(b.url)}" id="cbi-${esc(b.handle)}">` +
        `<span class="custom-brands-index__tile-mark">${markName(b.name, q)}</span>` +
        `<span class="custom-brands-index__tile-origin">${rank ? `<span class="custom-brands-index__rank-col">${rank}</span> · ` : ''}${esc(b.flag ? `${b.flag} ${b.origin}` : b.origin)}${b.year ? ` · ${b.year}` : ''}</span>` +
        '<span class="custom-brands-index__swap">' +
        `<span class="custom-brands-index__tile-tags">${tagHtml(b)}</span>` +
        (b.desc ? `<span class="custom-brands-index__tile-desc">${esc(b.desc)}</span>` : '') +
        '</span>' +
        (showCounts ? `<span class="custom-brands-index__tile-count custom-brands__count"><b>${b.count}</b> ${esc(productsLabel)}</span>` : '') +
        (via ? `<span class="custom-brands-index__via">matched ${esc(via)}</span>` : '') +
        '</a>'
      );
    }

    function items(list, q, ranked) {
      const fn = state.density === 'grid' ? tile : row;
      const cls = state.density === 'grid' ? 'custom-brands-index__tiles' : 'custom-brands-index__rows';
      return `<div class="${cls}">${list.map((b, i) => fn(b, q, ranked ? `#${i + 1}` : '')).join('')}</div>`;
    }

    const resultsEl = $('[data-cbi-results]');
    const countEl = $('[data-cbi-count]');
    const tokensEl = $('[data-cbi-tokens]');
    const resetEl = $('[data-cbi-reset]');

    function paintAbc(groups, off) {
      LETTERS.forEach((L) => {
        const btn = abcEl && abcEl.querySelector(`[data-letter="${L}"]`);
        if (!btn) return;
        const empty = off || !groups[L];
        btn.setAttribute('data-empty', empty ? 'true' : 'false');
        btn.setAttribute('aria-disabled', empty ? 'true' : 'false');
        if (empty) btn.removeAttribute('data-current');
      });
    }

    let letterFrame = 0;
    let selectedLetter = null;
    function syncCurrentLetter() {
      letterFrame = 0;
      const sections = $$('.custom-brands-index__letter[data-letter]');
      let current;
      if (sections.length) {
        const offset = parseFloat(getComputedStyle(sections[0]).scrollMarginTop) || 0;
        current = sections[0].dataset.letter;
        sections.forEach((section) => {
          if (section.getBoundingClientRect().top <= offset + 2) current = section.dataset.letter;
        });
      }
      if (selectedLetter && sections.some((section) => section.dataset.letter === selectedLetter)) current = selectedLetter;
      $$('.custom-brands-index__abc button').forEach((button) => {
        if (button.dataset.letter === current) {
          button.setAttribute('data-current', 'true');
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('data-current');
          button.removeAttribute('aria-current');
        }
      });
    }
    function observeLetters() {
      if (!letterFrame) letterFrame = requestAnimationFrame(syncCurrentLetter);
    }
    onDoc('scroll', observeLetters);
    function releaseSelectedLetter() {
      selectedLetter = null;
      observeLetters();
    }
    onDoc('wheel', releaseSelectedLetter);
    onDoc('touchstart', releaseSelectedLetter);
    onDoc('pointerdown', releaseSelectedLetter);
    onDoc('keydown', (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) releaseSelectedLetter();
    });

    const letterHead = (label, n) =>
      `<div class="custom-brands-index__letter-head"><span class="custom-brands-index__ltr">${esc(label)}</span><span class="custom-brands-index__cnt">${n}</span><span class="custom-brands-index__hr"></span></div>`;

    function render() {
      selectedLetter = null;
      observeLetters();
      const list = BRANDS.filter((b) => matches(b, state.q, state.types, state.setup));

      TYPES.forEach((t) => {
        const n = BRANDS.filter((b) => b.types.indexOf(t) > -1 && matches(b, state.q, [], state.setup)).length;
        const el = root.querySelector(`[data-n="${CSS.escape(t)}"]`);
        if (el) el.textContent = n;
        const chip = root.querySelector(`.custom-brands-index__chip[data-type="${CSS.escape(t)}"]`);
        if (chip) chip.disabled = n === 0 && state.types.indexOf(t) === -1;
      });

      if (!list.length) {
        resultsEl.innerHTML =
          '<div class="custom-brands-index__empty"><h3 class="h4">Nothing under that.</h3>' +
          `<p>No brand matches ${state.q ? `“${esc(state.q)}”` : 'these filters'}. Try searching by product — the index knows model names.</p>` +
          (SUGGESTIONS.length
            ? `<div class="custom-brands-index__suggest">${SUGGESTIONS.map((s) => `<button type="button" data-try="${esc(s)}">${esc(s)}</button>`).join('')}</div>`
            : '') +
          '</div>';
        countEl.textContent = '0 brands';
        paintAbc({}, true);
        return;
      }

      if (state.sort !== 'az') {
        const sorted = list.slice();
        if (state.sort === 'feat') {
          sorted.sort((a, b) => (a.position || 9999) - (b.position || 9999) || b.count - a.count || a.name.localeCompare(b.name));
        } else if (state.sort === 'prod') {
          sorted.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
        } else {
          sorted.sort((a, b) => (a.year || 9999) - (b.year || 9999) || a.name.localeCompare(b.name));
        }
        const heads = { feat: 'Best sellers first', prod: 'Most products first', old: 'Oldest first' };
        resultsEl.innerHTML = `<div class="custom-brands-index__letter">${letterHead(heads[state.sort], sorted.length)}${items(sorted, state.q, state.sort !== 'old')}</div>`;
        countEl.textContent = `${sorted.length} ${sorted.length === 1 ? 'brand' : 'brands'}`;
        paintAbc({}, true);
        return;
      }

      const groups = {};
      list.forEach((b) => (groups[b.letter] = groups[b.letter] || []).push(b));

      resultsEl.innerHTML = Object.keys(groups)
        .sort()
        .map(
          (L) =>
            `<section class="custom-brands-index__letter" id="cbi-letter-${L}" data-letter="${L}">${letterHead(L, groups[L].length)}${items(groups[L], state.q, false)}</section>`
        )
        .join('');

      countEl.textContent = `${list.length} ${list.length === 1 ? 'brand' : 'brands'}`;
      paintAbc(groups);
      observeLetters();
    }

    function paintTokens() {
      tokensEl.innerHTML =
        state.setup !== null
          ? `<span class="custom-brands-index__token">${esc(SETUPS[state.setup].name)} <button type="button" data-clear="setup" aria-label="Clear setup filter">×</button></span>`
          : '';
      resetEl.hidden = !(state.q || state.types.length || state.setup !== null || state.sort !== 'az');
    }

    function paintSetups() {
      SETUPS.forEach((s, i) => s.tile.setAttribute('aria-pressed', i === state.setup ? 'true' : 'false'));
    }

    function update() {
      paintTokens();
      render();
    }

    function resetAll() {
      state.q = '';
      state.types = [];
      state.setup = null;
      state.sort = 'az';
      if (qEl) qEl.value = '';
      if (sortEl) sortEl.value = 'az';
      $$('.custom-brands-index__chip').forEach((c) => c.setAttribute('aria-pressed', 'false'));
      paintSetups();
      update();
    }

    /* --------------------------------------------------------- events --- */
    let qTimer = null;
    const qEl = $('[data-cbi-q]');
    if (qEl) {
      qEl.addEventListener('input', (e) => {
        state.q = e.target.value;
        clearTimeout(qTimer);
        qTimer = setTimeout(update, 110);
      });
    }

    const sortEl = $('[data-cbi-sort]');
    if (sortEl) {
      sortEl.addEventListener('change', (e) => {
        state.sort = e.target.value;
        update();
      });
    }

    const densityEl = $('[data-cbi-density]');
    if (densityEl) {
      const paintDensity = () =>
        $$('[data-cbi-density] button').forEach((b) =>
          b.setAttribute('aria-pressed', b.dataset.density === state.density ? 'true' : 'false')
        );
      paintDensity();
      densityEl.addEventListener('click', (e) => {
        const b = e.target.closest('[data-density]');
        if (!b || b.dataset.density === state.density) return;
        state.density = b.dataset.density;
        paintDensity();
        render();
      });
    }

    if (chipsEl) {
      chipsEl.addEventListener('click', (e) => {
        const chip = e.target.closest('.custom-brands-index__chip');
        if (!chip || chip.disabled) return;
        const t = chip.dataset.type;
        const i = state.types.indexOf(t);
        if (i > -1) state.types.splice(i, 1);
        else state.types.push(t);
        chip.setAttribute('aria-pressed', i > -1 ? 'false' : 'true');
        update();
      });
    }

    const barEl = $('[data-cbi-bar]');
    if (SETUPS.length) {
      onDoc('click', (e) => {
        const tile = e.target.closest('[data-custom-brands-setup]');
        if (!tile || tile.dataset.setupIndex === undefined) return;
        const i = parseInt(tile.dataset.setupIndex, 10);
        state.setup = state.setup === i ? null : i;
        paintSetups();
        update();
        if (state.setup !== null && barEl) barEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    tokensEl.addEventListener('click', (e) => {
      if (!e.target.closest('[data-clear]')) return;
      state.setup = null;
      paintSetups();
      update();
    });

    resetEl.addEventListener('click', resetAll);

    if (abcEl) {
      abcEl.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b || b.dataset.empty === 'true') return;
        const sec = root.querySelector(`#cbi-letter-${b.dataset.letter}`);
        if (sec) {
          selectedLetter = b.dataset.letter;
          observeLetters();
          sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    resultsEl.addEventListener('click', (e) => {
      const t = e.target.closest('[data-try]');
      if (t) {
        e.preventDefault();
        state.q = t.dataset.try;
        if (qEl) qEl.value = state.q;
        update();
      }
    });

    /* Sticky offsets follow the real bar height, which changes as it wraps. */
    function syncBar() {
      if (!barEl) return;
      const h = barEl.offsetHeight;
      if (h) root.style.setProperty('--cb-barh', `${h}px`);
      observeLetters();
    }
    window.addEventListener('resize', syncBar);
    const barObserver = window.ResizeObserver && barEl ? new ResizeObserver(syncBar) : null;
    if (barObserver) barObserver.observe(barEl);
    const teardown = root.cbiTeardown;
    root.cbiTeardown = () => {
      teardown();
      cancelAnimationFrame(letterFrame);
      window.removeEventListener('resize', syncBar);
      if (barObserver) barObserver.disconnect();
    };

    /* ------------------------------------------------- ⌘K quick find ---- */
    const scrim = $('[data-cbi-scrim]');
    if (scrim) {
      const pq = $('[data-cbi-pq]');
      const plist = $('[data-cbi-plist]');
      let pIndex = 0;
      let pResults = [];

      const paintPalette = (q) => {
        pResults = BRANDS.filter((b) => matches(b, q, [], null)).slice(0, 40);
        pIndex = 0;
        plist.innerHTML = pResults.length
          ? pResults
              .map((b, i) => {
                const via = viaLabel(b, q);
                return (
                  `<div class="custom-brands-index__prow" role="option" aria-selected="${i === 0}" data-i="${i}">` +
                  `<span class="custom-brands-index__pn">${markName(b.name, q)}</span>` +
                  `<span class="custom-brands-index__pm">${via ? `${esc(via)} · ` : ''}${esc(b.types.join(' / '))}${showCounts ? ` · ${b.count} ${esc(productsLabel)}` : ''}</span></div>`
                );
              })
              .join('')
          : '<div class="custom-brands-index__prow"><span class="custom-brands-index__pm">No brand matches that.</span></div>';
      };
      const openPalette = () => {
        scrim.hidden = false;
        pq.value = '';
        paintPalette('');
        pq.focus();
      };
      const closePalette = () => {
        scrim.hidden = true;
      };
      const movePalette = (d) => {
        if (!pResults.length) return;
        pIndex = (pIndex + d + pResults.length) % pResults.length;
        const rows = plist.querySelectorAll('.custom-brands-index__prow');
        rows.forEach((r, i) => r.setAttribute('aria-selected', i === pIndex));
        if (rows[pIndex]) rows[pIndex].scrollIntoView({ block: 'nearest' });
      };
      const choosePalette = (i) => {
        const b = pResults[i];
        if (b) window.location.href = b.url;
      };

      pq.addEventListener('input', (e) => paintPalette(e.target.value));
      plist.addEventListener('click', (e) => {
        const r = e.target.closest('.custom-brands-index__prow');
        if (r && r.dataset.i !== undefined) choosePalette(parseInt(r.dataset.i, 10));
      });
      scrim.addEventListener('mousedown', (e) => {
        if (e.target === scrim) closePalette();
      });

      onDoc('keydown', (e) => {
        const openNow = !scrim.hidden;
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
          e.preventDefault();
          if (openNow) closePalette();
          else openPalette();
          return;
        }
        if (openNow) {
          if (e.key === 'Escape') {
            e.preventDefault();
            closePalette();
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            movePalette(1);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            movePalette(-1);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            choosePalette(pIndex);
          }
          return;
        }
        const tag = (e.target.tagName || '').toLowerCase();
        const typing = tag === 'input' || tag === 'select' || tag === 'textarea' || e.target.isContentEditable;
        if (e.key === '/' && !typing && qEl) {
          e.preventDefault();
          qEl.focus();
        }
        if (e.key === 'Escape' && e.target === qEl) {
          state.q = '';
          qEl.value = '';
          update();
        }
      });
    }

    update();
    syncBar();
  }

  const boot = (scope) => scope.querySelectorAll('[data-custom-brands-index]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);

  /* Theme editor: re-init when the index or a setups section is re-rendered,
     so setup tiles and the index always point at each other's live DOM. */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-custom-brands-index]').forEach((el) => {
      if (typeof el.cbiTeardown === 'function') el.cbiTeardown();
    });
    boot(document);
  });
})();
